<script>
import AqKpi from '@/components/aqua/AqKpi.vue'
import AquaSelect from '@/views/account/reporting/aqua/AquaSelect.vue'

const CANCELLED_STATUS_CODE = '9'
const ALL = 'all'
const ACTIVE = 'active'

export default {
	name: 'PermissionsFishingPlans',
	components: { AqKpi, AquaSelect },
	props: {
		licenses: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			year: ALL,
			status: ACTIVE,
			fish: ALL,
		}
	},
	computed: {
		years() {
			const years = this.licenses.map((license) => this.issueYear(license)).filter(Boolean)

			return [...new Set(years)].sort((a, b) => b - a)
		},
		yearOptions() {
			return [{ value: ALL, text: this.$t('permission_plans.all_years') }, ...this.years.map((year) => ({ value: year, text: String(year) }))]
		},
		statusOptions() {
			const options = new Map()

			this.licenses.forEach((license) => {
				const code = this.statusCode(license)

				if (code !== null && !options.has(code)) {
					options.set(code, { value: code, text: this.statusName(license) || code })
				}
			})

			return [
				{ value: ACTIVE, text: this.$t('permission_plans.status_active') },
				{ value: ALL, text: this.$t('permission_plans.all_statuses') },
				...options.values(),
			]
		},
		scopedLicenses() {
			return this.licenses.filter((license) => {
				return (this.year === ALL || this.issueYear(license) === this.year) && this.matchesStatus(license)
			})
		},
		fishTotals() {
			const totals = new Map()

			this.scopedLicenses.forEach((license) => {
				const seen = new Set()

				this.plans(license).forEach((plan) => {
					const key = this.fishKey(plan)
					const total = totals.get(key) || { key, name: this.scalar(plan.FishType) || '—', volume: 0, licenses: 0 }

					total.volume += this.toNumber(plan.Count)
					if (!seen.has(key)) {
						total.licenses += 1
						seen.add(key)
					}
					totals.set(key, total)
				})
			})

			return [...totals.values()].sort((a, b) => b.volume - a.volume)
		},
		totalVolume() {
			return this.fishTotals.reduce((sum, fish) => sum + fish.volume, 0)
		},
		fishOptions() {
			return [
				{ value: ALL, text: `${this.$t('permission_plans.all_fish')} — ${this.volume(this.totalVolume)}` },
				...this.fishTotals.map((fish) => ({ value: fish.key, text: `${fish.name} — ${this.volume(fish.volume)}` })),
			]
		},
		selectedFish() {
			return this.fishTotals.find((fish) => fish.key === this.fish) || null
		},
		filteredLicenses() {
			if (!this.selectedFish) return this.scopedLicenses

			return this.scopedLicenses.filter((license) => this.plans(license).some((plan) => this.fishKey(plan) === this.fish))
		},
	},
	watch: {
		licenses: {
			immediate: true,
			handler() {
				this.resetFilters()
			},
		},
		fishTotals(totals) {
			if (this.fish !== ALL && !totals.some((fish) => fish.key === this.fish)) this.fish = ALL
		},
		filteredLicenses: {
			immediate: true,
			handler(list) {
				this.$emit('change', list)
			},
		},
	},
	methods: {
		resetFilters() {
			const currentYear = new Date().getFullYear()

			this.year = this.years.includes(currentYear) ? currentYear : this.years[0] || ALL
			this.status = ACTIVE
			this.fish = ALL
		},
		matchesStatus(license) {
			const code = this.statusCode(license)

			if (this.status === ALL) return true
			if (this.status === ACTIVE) return code !== CANCELLED_STATUS_CODE

			return code === this.status
		},
		selectFish(key) {
			this.fish = this.fish === key ? ALL : key
		},
		percentOf(value) {
			return this.totalVolume > 0 ? (value / this.totalVolume) * 100 : 0
		},
		share(value) {
			return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(this.percentOf(value))}%`
		},
		volume(value) {
			return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 }).format(value)} ${this.$t('permission_plans.unit')}`
		},
		plans(license) {
			const plans = license.FishingPlans

			return Array.isArray(plans) ? plans : plans ? [plans] : []
		},
		fishKey(plan) {
			return String(this.scalar(plan.Code) || this.scalar(plan.FishType) || '')
				.trim()
				.toLowerCase()
		},
		scalar(value) {
			const scalar = Array.isArray(value) ? value[0] : value

			return scalar === undefined || scalar === null || scalar === '' ? null : String(scalar)
		},
		issueYear(license) {
			const year = new Date(this.scalar(license.IssueDate)).getFullYear()

			return Number.isNaN(year) ? null : year
		},
		statusCode(license) {
			return this.scalar(license.Status?.Code)
		},
		statusName(license) {
			const locale = this.$i18n.locale === 'kz' ? 'NameKz' : 'NameRu'

			return this.scalar(license.Status?.[locale])
		},
		toNumber(value) {
			const number = parseFloat(
				String(this.scalar(value) || 0)
					.replace(/\s/g, '')
					.replace(',', '.'),
			)

			return Number.isNaN(number) ? 0 : number
		},
	},
}
</script>

<template>
	<section class="pp">
		<div class="pp-filters">
			<AquaSelect v-model="year" class="pp-filter" :label="$t('permission_plans.year')" :options="yearOptions" />
			<AquaSelect v-model="status" class="pp-filter" :label="$t('permission_plans.status')" :options="statusOptions" />
			<AquaSelect
				v-model="fish"
				class="pp-filter"
				:label="$t('permission_plans.fish')"
				:options="fishOptions"
				:disabled="!fishTotals.length"
			/>
		</div>

		<div class="pp-kpis">
			<AqKpi :label="$t('permission_plans.licenses')" :value="filteredLicenses.length" />
			<AqKpi
				tone="success"
				:label="selectedFish ? $t('permission_plans.fish_volume', { fish: selectedFish.name }) : $t('permission_plans.total_volume')"
				:value="volume(selectedFish ? selectedFish.volume : totalVolume)"
				:progress="selectedFish ? percentOf(selectedFish.volume) : null"
				:hint="selectedFish ? $t('permission_plans.share_hint', { share: share(selectedFish.volume) }) : ''"
			/>
			<AqKpi :label="$t('permission_plans.species')" :value="fishTotals.length" />
		</div>

		<div v-if="fishTotals.length" class="pp-breakdown">
			<div class="pp-breakdown__head">
				<span>{{ $t('permission_plans.fish') }}</span>
				<span>{{ $t('permission_plans.share') }}</span>
				<span class="num">{{ $t('permission_plans.volume') }}</span>
				<span class="num">{{ $t('permission_plans.licenses_short') }}</span>
			</div>
			<button
				v-for="item in fishTotals"
				:key="item.key"
				type="button"
				class="pp-row"
				:class="{ 'is-active': fish === item.key, 'is-dimmed': selectedFish && fish !== item.key }"
				:aria-pressed="String(fish === item.key)"
				@click="selectFish(item.key)"
			>
				<span class="pp-row__name">{{ item.name }}</span>
				<span class="pp-row__share">
					<span class="pp-row__track"><span :style="{ width: `${percentOf(item.volume)}%` }"></span></span>
					<span class="pp-row__percent">{{ share(item.volume) }}</span>
				</span>
				<span class="num pp-row__volume">{{ volume(item.volume) }}</span>
				<span class="num">{{ item.licenses }}</span>
			</button>
			<div class="pp-row pp-row--total">
				<span class="pp-row__name">{{ $t('permission_plans.total') }}</span>
				<span></span>
				<span class="num pp-row__volume">{{ volume(totalVolume) }}</span>
				<span class="num">{{ scopedLicenses.length }}</span>
			</div>
		</div>
		<p v-else class="pp-empty">{{ $t('permission_plans.empty') }}</p>
	</section>
</template>

<style scoped>
.pp {
	margin-bottom: 20px;
}
.pp-filters {
	display: grid;
	grid-template-columns: 160px 280px minmax(260px, 380px);
	gap: 14px 16px;
	margin-bottom: 18px;
}
.pp-filter {
	width: 100%;
	min-width: 0;
}
.pp-filter ::v-deep .aqua-select__label {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.pp-filter ::v-deep .aqua-select__control {
	width: 100%;
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
}
.pp-kpis {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 18px;
}
.pp-breakdown {
	border: 1px solid var(--aq-border);
	border-radius: var(--aq-radius);
	background: var(--aq-surface);
	overflow: hidden;
	font-variant-numeric: tabular-nums;
}
.pp-breakdown__head,
.pp-row {
	display: grid;
	grid-template-columns: minmax(140px, 1.2fr) minmax(160px, 2fr) 120px 110px;
	align-items: center;
	gap: 16px;
	width: 100%;
	padding: 10px 20px;
	text-align: left;
}
.pp-breakdown__head {
	border-bottom: 1px solid var(--aq-border);
	background: #f8fafd;
	color: var(--aq-ink-2);
	font-size: 12px;
	font-weight: 600;
}
.pp-row {
	border-bottom: 1px solid var(--aq-border);
	color: var(--aq-ink);
	font-size: 14px;
	cursor: pointer;
	transition: background 0.15s var(--aq-ease), opacity 0.15s var(--aq-ease);
}
.pp-row:hover {
	background: #f8fbff;
}
.pp-row:focus-visible {
	outline: none;
	box-shadow: inset 0 0 0 2px rgba(21, 127, 238, 0.35);
}
.pp-row.is-active {
	background: var(--aq-primary-soft);
}
.pp-row.is-active .pp-row__name {
	color: var(--aq-primary);
}
.pp-row.is-dimmed {
	opacity: 0.55;
}
.pp-row--total {
	border-bottom: 0;
	background: #f8fafd;
	font-weight: 700;
	cursor: default;
}
.pp-row--total:hover {
	background: #f8fafd;
}
.pp-row__name {
	font-weight: 600;
}
.pp-row__share {
	display: flex;
	align-items: center;
	gap: 10px;
}
.pp-row__track {
	flex: 1 1 auto;
	height: 6px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.pp-row__track span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-primary);
	transition: width 0.3s var(--aq-ease);
}
.pp-row__percent {
	min-width: 48px;
	color: var(--aq-muted);
	font-size: 13px;
	text-align: right;
}
.pp-row__volume {
	font-weight: 600;
}
.num {
	text-align: right;
	white-space: nowrap;
}
.pp-empty {
	margin: 0;
	padding: 18px 20px;
	border: 1px dashed var(--aq-border-strong);
	border-radius: var(--aq-radius);
	color: var(--aq-muted);
	font-size: 14px;
	text-align: center;
}
@media (max-width: 1100px) {
	.pp-filters,
	.pp-kpis {
		grid-template-columns: minmax(0, 1fr);
	}
	.pp-breakdown__head,
	.pp-row {
		grid-template-columns: minmax(110px, 1fr) minmax(100px, 1fr) 100px 60px;
		gap: 10px;
		padding: 10px 14px;
	}
}
</style>
